import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { strings, getCurrentLocale } from 'APP_ROOT/localization';
import ReactTable from 'react-table'; /* for the report tables */
import { csvFormat } from 'd3-dsv';
import 'react-table/react-table.css';
import { DispatchHelpers  as AssetMonitoringDispatchHelpers}  from '../../../data/assetMonitoring';
import ErrorMessage from 'APP_ROOT/components/ErrorMessage';
import RavenUtil from 'APP_ROOT/components/common/ravenUtil';
import geofencesSelectors from 'APP_ROOT/data/geofences/selectors';
import { loadActiveGeofences }  from 'APP_ROOT/data/geofences/actions';
import MaintenanceMessage from '../MaintenanceMessage';
import ProgressOverlay from '../../../components/common/ProgressOverlay';
import LocationLabel from 'APP_ROOT/components/pages/dashboard/LocationLabel';
import LocationService from '../../../services/locationService';


const convertToAssetReportItem = (asset) => {
    return {
        assetUuid: asset.assetUuid,
        name: asset.name,
        make: asset.make,
        model: asset.model,
        serialNo: asset.serialNo,
        description: asset.description,
        beaconUuid: asset.beaconUuid,
        batteryPercentage: asset.batteryPercentage,
        ts: asset.ts,
        carPersona: asset.carPersona,
        enclosureSerialNo: asset.enclosureSerialNo,
        latitude: asset.latitude,
        longitude: asset.longitude,
        geofences: 'pending',
    };
}

const DATA_TYPES = {
    ASSETS: 'assets',
    BEACONS: 'beacons',
    GEOFENCES: 'geofences'
}

class ReportsSection extends React.PureComponent {

    static propTypes = {
        assets: PropTypes.array.isRequired,
        beacons: PropTypes.array.isRequired,
        activeGeofenceIds: PropTypes.array.isRequired,
        wrappingGeofences: PropTypes.func.isRequired,
        loadAllAssets: PropTypes.func.isRequired,
        loadAllBeacons: PropTypes.func.isRequired,
        loadActiveGeofences: PropTypes.func.isRequired,
        user: PropTypes.object.isRequired,
    };

    static defaultProps = {
        assets: [],
        beacons: [],
        activeGeofenceIds: [],
    };

    state = {
        assets: [],
        error: null,
        loadingStatus: {
            assets: true,
            beacons: true,
            geofences: true
        }
    }

    abortController = null;
    
    
    componentDidMount() {
        this.abortController = new AbortController();

        this.loadAssets();

        this.loadBeacons();

        this.loadGeofences();
    }

    componentWillUnmount() {
        if(this.abortController) {
            this.abortController.abort();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (!this.isReady(prevState) && this.isReady()) {
            this.loadAssetsDetails();
        }
    }
    
    isReady = (state = this.state) => {
        const { loadingStatus } = state;
        return !loadingStatus.assets && !loadingStatus.beacons && !loadingStatus.geofences;
    }

    loadAssets = () => {
        if (this.props.assets.length > 0) {
            this.setAssets();
            this.markDataLoaded(DATA_TYPES.ASSETS);
        } else {
            this.props.loadAllAssets()
                .then(() => {
                    this.setAssets();
                    this.markDataLoaded(DATA_TYPES.ASSETS);
                })
                .catch(this.handleError);
        }
    }

    loadBeacons = () => {
        if (this.props.beacons.length > 0) {
            this.markDataLoaded(DATA_TYPES.BEACONS);
        } else {
            this.props.loadAllBeacons()
                .then(() => this.markDataLoaded(DATA_TYPES.BEACONS))
                .catch(this.handleError);
        }
    }

    loadGeofences = () => {
        if (this.props.activeGeofenceIds.length > 0) {
            this.markDataLoaded(DATA_TYPES.GEOFENCES);
        } else {
            this.props.loadActiveGeofences(this.props.user.stage)
                .then(() => this.markDataLoaded(DATA_TYPES.GEOFENCES))
                .catch(this.handleError);
        }
    }

    markDataLoaded = (dataType) => {
        if (this.abortController.signal.aborted) return;

        this.setState(prevState => ({
            loadingStatus: {
                ...prevState.loadingStatus,
                [dataType]: false
            }
        }));
    }

    handleError = (error) => {
        this.setState({ error });
    }

    setAssets = () => {
        if (this.abortController.signal.aborted) return;

        const assets = this.props.assets.map(asset => convertToAssetReportItem(asset));
        this.setState({ assets: assets });
    }

    loadAssetsDetails = async () => {        
        this.setAssetsGeofences();
        await this.enrichAssetsWithAddresses();
    }

    enrichAssetsWithAddresses = async () => {
        const { assets } = this.state;
        
        for (let i = 0; i < assets.length; i++) {
            const asset = assets[i];
            if (asset.latitude && asset.longitude) {
                const address = await LocationService.reverseGeocode(asset.latitude, asset.longitude);
                
                // Update state row by row
                this.setState(prevState => {
                    const newAssets = [...prevState.assets];
                    newAssets[i] = { ...newAssets[i], address };
                    return { assets: newAssets };
                });
            }
        }
    }
 
    setAssetsGeofences = () => {
        const assets = this.state.assets;

        const assetsWithGeofences = assets.map(currentAsset => {
            if (!currentAsset.longitude || !currentAsset.latitude) {

                return { 
                    ...currentAsset, 
                    geofences: '' 
                };
            }

            const geofences = this.props.wrappingGeofences(currentAsset.longitude, currentAsset.latitude);
            return {
                ...currentAsset, 
                geofences: geofences.map(g => g.name).join(', ')
            };
        });

        this.setState({ assets: assetsWithGeofences });

    }

    render() {
        if(this.state.error) {
            return this.renderError(this.state.error);
        }

        if(!this.isReady()) {
            return <div>{strings.reports_asset_preparing}</div>;
        }

        if(this.state.assets.length <= 0) {
            return <div>{strings.reports_no_data_for_selected_time_period}</div>;
        }

        return <ReportTable
                    resultSet={ this.state.assets}
                    beacons={this.props.beacons}
                />;
    }

    renderError = error => {
        if(error && error.message && error.message.match(/maintenance/i)) {
            return <MaintenanceMessage message={error.message} />;
        } else {
            return <ErrorMessage error={error} />;
        }
    }
}

class ReportTable extends React.PureComponent {

    getDimensionColumn = (name, field, overrides = {}) => {
        return ({
            Header: name,
            Placeholder: strings.formatString(strings.reports_header_filter_x, name.toLowerCase()),
            accessor: d => d[field],
            id: field,
            ...overrides
        });
    };

    generateExportURI = (columns, rawRows) => {
        const outputRows = rawRows.map((row) => {
            const outRow = {};
            columns.forEach( (col) => {
                if((col.show || col.show === undefined || col.export)
                    && (col.export || col.export === undefined)) {
                        outRow[col.Header] = (typeof col.accessor === 'string' ? row[col.accessor] : col.accessor(row));
                }
            });
            return outRow;
        });

        const BOM='\uFEFF';

        return `data:text/csv;charset=utf-8,${BOM}${encodeURIComponent(csvFormat(outputRows))}`;
    }

    render() {
        const { resultSet } = this.props;

        var dims = [];
        var sort = [];

        dims.push(this.getDimensionColumn(strings.reports_header_asset_name,'name', {
            filterable: true,
            Cell: d => {
                return <a href={`/${getCurrentLocale()}/dashboard/assets/${d.original.assetUuid}`} target='_blank'>{d.value}</a>;
            },
            filterMethod: (filter, row) => {
                return row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
            }
        }));

        dims.push(this.getDimensionColumn(strings.reports_header_asset_details,'details', {
            filterable: true,
            Cell: d => {
                return <ul>
                    <li>{d.value[0]}</li>
                    <li>{d.value[1]}</li>
                    <li>{d.value[2]}</li>
                    <li>{d.value[3]}</li>
                </ul>
            },
            accessor: row => {
                return [row.make, row.model, row.serialNo, row.description];
            },
            filterMethod: (filter, row) => {
                const value = filter.value.toLowerCase();
                
                if(row.details && row.details.some(d => d != undefined && d.toLowerCase().includes(value))) return true;

                return false;
            }
        }));

        dims.push(this.getDimensionColumn(strings.reports_header_asset_beacon,'beacon', {
            Cell: d => <span>{d.value}</span>,
            accessor: row => {
                if(row.beaconUuid){
                    const beacon = this.props.beacons.find(b => b.beaconUuid === row.beaconUuid);
                    return beacon ? beacon.btMac : 'Not Assigned';
                }else{
                    return 'Not Assigned';
                }
            }
        }));
        
        dims.push(this.getDimensionColumn(strings.reports_header_asset_battery,'batteryPercentage', {
            Cell: d => {
                let color;
                switch(true){
                    case d.value <= 20:
                        color = 'text-danger';
                        break;
                    case d.value <= 30:
                        color = 'text-warning';
                        break;
                    case d.value > 30:
                        color = 'text-success';
                        break;
                    default:
                        color = 'text-secondary';
                        break;
                }

                return <span className={`${color}`}>{d.value !== undefined ? `${d.value}%` : ``}</span>;
            },
        }));

        dims.push(this.getDimensionColumn(strings.reports_header_asset_last_report_time,'lastReportTime', {
            Cell: d => {
                return <span>{d.value}</span>;
            },
            accessor: row => {
                if(row.ts){
                    return RavenUtil.toDateString(new Date(row.ts * 1000))
                }
                return '';
            },
        }));

        dims.push(this.getDimensionColumn(strings.reports_header_asset_reported_by,'reportedBy', {
            filterable: true,
            Cell: d => {
                return <span>{d.value}</span>;
            },
            accessor: row => {
                if(row.carPersona && row.enclosureSerialNo){
                    return `${row.carPersona} [${row.enclosureSerialNo}]`;
                }
                return '';
            },
            filterMethod: (filter, row) => {
                const value = filter.value.toLowerCase();
                
                if(row.reportedBy && row.reportedBy.toLowerCase().includes(value)) return true;
                
                return false;
            }
        }));

        dims.push(this.getDimensionColumn(strings.reports_header_asset_location, 'location', {
            Cell: d => {
                if (!d.value) {
                    return <LocationLabel
                        location={{ lat: d.original.latitude, lon: d.original.longitude }}
                        withStreetNumber={true}
                        addMapLink={true}
                    />;
                }
                return <span>{d.value}</span>;
            },
            accessor: d => d.address,
            id: 'address',
            export: true,
        }));

        dims.push(this.getDimensionColumn(strings.reports_header_asset_geofences,'geofences', {
            filterable: true,
            Cell: d => {
                if(!d.value){
                    return '';
                }

                if(d.value === 'pending') {
                    return <div style={{position: 'relative'}}><ProgressOverlay /></div>;
                }

                return <span>{d.value}</span>;
            },
            filterMethod: (filter, row) => {
                const value = filter.value.toLowerCase();
                if(row.geofences && row.geofences.toLowerCase().includes(value)) return true;
                
                return false;
            }
        }));
        
        sort.push({id:'ts', desc: false});



        const columns = dims;


        const rows = resultSet;

        if(!rows || rows.length <= 0)
        {
            return (
                <div>
                    <h4>{strings.reports_no_data_for_selected_time_period}</h4>
                </div>
            );
        }

        const csvUrl = this.generateExportURI(columns, rows);

        return (
            <div>
                <ReactTable
                    data = { rows }
                    filterable = {false}
                    columns = {columns}
                    noDataText={strings.reports_no_data_for_selected_time_period}
                    showPagination = {false}
                    defaultPageSize = {Number.MAX_SAFE_INTEGER} // RAV-1942, step one (effectively "set the page size to the number of rows" returned from the database)
                    minRows = {0} /* 0, do not show empty filler rows */
                    defaultSorted={[
                        ...sort,
                    ]}
                    getTrProps={(state, rowInfo, column) => {
                        return { className: '-padRow'};
                    }}
                />
                <div className='text-right mt-1'>
                    <a
                        className='btn btn-outline-blue btn-sm'
                        download="asset-report.csv"
                        id="export-link"
                        href={csvUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {strings.action_export_CSV}
                    </a>
                </div>
            </div>
        );
    }
}


const mapStateToProps = state => {
    return {
        assets: state.data.assetMonitoring.assets,
        beacons: state.data.assetMonitoring.beacons,
        wrappingGeofences: (log, lat) => geofencesSelectors.wrappingGeofences(state, [log, lat]),
        activeGeofenceIds: geofencesSelectors.activeGeofenceIds(state),
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        loadAllAssets: (stage) => {
            return dispatch(AssetMonitoringDispatchHelpers.loadAssets());
        },
        loadAllBeacons: (stage) => {
            return dispatch(AssetMonitoringDispatchHelpers.loadBeacons());
        },
        loadActiveGeofences: (stage) => {
            return dispatch(loadActiveGeofences(stage));
        }
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(ReportsSection);
